import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Q from "q";
import Resources = require("Dashboards/Scripts/Resources/TFS.Resources.Dashboards");
import { registerLWPComponent } from "VSS/LWP";
import { LoadingStatus } from "ModernWidgetContracts/Internal/ConfigProps";
import { LegacyConfigProps } from "ModernWidgetContracts/Internal/LegacyConfigProps";
import { ErrorMessageHelper } from "Dashboards/Scripts/Common";
import { WidgetSettings, IWidgetConfiguration, IWidgetConfigurationContext, WidgetStatusType, SaveStatus } from "TFS/Dashboards/WidgetContracts";
import Contributions_Controls = require("VSS/Contributions/Controls");
import { isFirstPartyContribution, loadContribution, interpretStatus, extractRelativeContributionId } from "./LegacyHostUtilities";
import { WidgetEvent } from "TFS/Dashboards/WidgetHelpers";
import { NotifyResult, EventArgs, CustomSettings } from "TFS/Dashboards/WidgetContracts";
import { format } from "VSS/Utils/String";
import { IGeneralSettings } from "Dashboards/Scripts/Contracts";

// Note: Legacy has a hard, compile-time only dependency on Config Host Project to expose contract definition for ConfigHosts.
// New plat has an run-time only dependency (through LWP) on Legacy Dashboard Stack for this Adapter and it's dependencies, with no build relationship

interface LegacyWidgetHostState { }

/** Exposes a New-Platform compatible entry point for Hosting of Legacy "Custom" Configuration.
 *
 * Responsible for transforming config data to legacy format, and transforming callbacks into modern shape.
 */
export class LegacyConfigHostAdapter extends React.Component<LegacyConfigProps, LegacyWidgetHostState> {
    public static readonly componentType = "LegacyConfigHostAdapter";
    private contributedControl: IWidgetConfiguration;

    public constructor(props: LegacyConfigProps, state: LegacyWidgetHostState) {
        super(props);
    }

    public render(): JSX.Element {
        return <div className="classic-config-container" />;
    }

    /** React to updates to respond to requests for widget Validation, to honor legacy pattern of validation on save */
    public componentDidUpdate(prevProps: Readonly<LegacyConfigProps>, prevState: Readonly<LegacyWidgetHostState>): void {
        if (prevProps.saveValidationTimeStamp !== this.props.saveValidationTimeStamp) {

            if (this.contributedControl.onSave() != null) {
                this.contributedControl.onSave().then((value: SaveStatus) => {
                    const latestConfig = Object.assign({}, this.props.config, {
                        settings: value.customSettings.data,
                        settingsVersion: value.customSettings.version || this.props.config.settingsVersion
                    });
                    this.props.parentProps.onValidationResponse(latestConfig, value.isValid);
                }, () => {
                    this.props.parentProps.onValidationResponse(this.props.config, false);
                })
            } else {
                //If no validation implementation is given, then the widget is implicitly in a valid state.
                this.props.parentProps.onValidationResponse(this.props.config, false);
            }

        }

        this.notifyCustomConfigOfGeneralSettingsChange(prevProps);
    }

    //Note: As legacy plat only generated up to ES5, we do not use Async-await for debuggability
    public componentDidMount(): void {
        let container = ReactDOM.findDOMNode(this);
        let $wrappedContainer = $(container);

        if (this.props != null) {
            loadContribution(this.props.config.configurationContributionId).then((legacyExtensionContribution) => {
                loadConfig($wrappedContainer, legacyExtensionContribution, this.props).then((control) => {
                    this.contributedControl = control;

                    const configSettings = packLegacySettings(this.props);
                    const configurationContext: IWidgetConfigurationContext = {
                        notify: this.notify.bind(this)
                    };

                    control.load(configSettings, configurationContext).then((loadOutcome) => {
                        if (loadOutcome.statusType !== WidgetStatusType.Success) {
                            this.passErrorToHost(Resources.ConfigHostAdapter_LoadFailure, "VS403685", loadOutcome.state);
                        }
                        this.props.parentProps.onConfigLoaded(interpretStatus(loadOutcome));

                    }, (error) => {
                        this.passErrorToHost(Resources.ConfigHostAdapter_LoadFailure, "VS403684", error);
                    });
                }, (error) => {
                    this.passErrorToHost(Resources.ConfigHostAdapter_ContributionLoadError, "VS403683", error);
                });
            }, (error) => {
                this.passErrorToHost(Resources.ConfigHostAdapter_ContributionLoadError, "VS403682", error);
            });
        }
    }

    private notifyCustomConfigOfGeneralSettingsChange(prevProps: Readonly<LegacyConfigProps>): void {
        if (prevProps.config.size.columnSpan == this.props.config.size.columnSpan &&
            prevProps.config.size.rowSpan == this.props.config.size.rowSpan &&
            prevProps.config.name == this.props.config.name
        ) {
            return;
        }

        const newSettings: EventArgs<IGeneralSettings> = {
            data: {
                WidgetName: this.props.config.name,
                WidgetSize: {
                    columnSpan: this.props.config.size.columnSpan,
                    rowSpan: this.props.config.size.rowSpan,
                },
            },
        };

        if (this.contributedControl.listen) {
            this.contributedControl.listen(WidgetEvent.GeneralSettingsChanged, newSettings);
        }
    }

    private passErrorToHost(errorTextFormat: string, vsErrorId: string, errorObject: any) {
        const errorDetail = ErrorMessageHelper.parseErrorToString(errorObject);
        this.props.parentProps.onConfigLoaded({
            outcome: LoadingStatus.failure,
            outcomeDescription: format(errorTextFormat, vsErrorId, errorDetail)
        });
    }

    private notify = function <T>(this: LegacyConfigHostAdapter, event: string, eventArgs: EventArgs<T>): IPromise<NotifyResult> {
        const eventPayload: unknown = eventArgs && eventArgs.data;
        if (event === WidgetEvent.ConfigurationChange) {
            const newLegacySettings = eventPayload as CustomSettings;
            const newSettings = Object.assign({}, this.props.config, { settings: newLegacySettings.data, settingsVersion: newLegacySettings.version });
            this.props.parentProps.onUpdate(newSettings);
        } else {
            //Unsupported Contract.
            throw new Error(Resources.ConfigHostAdapter_UnsupportedAPI);
        }

        //Responses from notify pattern are completely obsolete under New Platform.
        const result: NotifyResult = {
            getResponse: undefined
        };

        return Q(result);
    }
}
registerLWPComponent(LegacyConfigHostAdapter.componentType, LegacyConfigHostAdapter);

export function loadConfig($container: JQuery, legacyExtensionContribution: IExtensionContribution, props: LegacyConfigProps): IPromise<IWidgetConfiguration> {
    const isFirstParty = isFirstPartyContribution(legacyExtensionContribution);

    // instanceId for contributed controls (note that first party configs dont need one as their registration mechanism is done via
    // the registerContent mechanism which uses the hidden initializeConfig handshake options. We should consider transforming
    // first party widget configs to be registered similar 3rd party ones to reduce code branches and simplify code).
    let instanceId: string | null = null;
    if (!isFirstParty) {
        // `instanceId` is the relative contribution ID.
        instanceId = extractRelativeContributionId(props.config.configurationContributionId);
    }

    //Pass secret contracts expected by legacy first party Widgets
    const configOptions = isFirstParty ? {
        ...props.legacyRuntimeProps,
        teamContext: props.teamContext,
        widgetTypeId: props.config.contributionId
    } : null;

    return Contributions_Controls.createContributedControl<IWidgetConfiguration>(
        $container,
        legacyExtensionContribution,
        configOptions,
        null,
        instanceId,
        {
            teamContextOverride: props.teamContext
        });
}


export function packLegacySettings(configProps: LegacyConfigProps): WidgetSettings {
    return {
        name: configProps.config.name,
        customSettings: {
            data: configProps.config.settings,
            version: configProps.config.settingsVersion
        },
        size: configProps.config.size
    };
}